import { useState, useEffect} from "react";
import api from "../../services/api";
import DataTable from "react-data-table-component"; 
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faSquareMinus, faSquarePlus } from "@fortawesome/free-solid-svg-icons";
import EditModal from "../ui/EditModal";
import FieldDefinition from "../dynamicsFields/FieldDefinition.js";
import { TIPOS_DATO } from "../dynamicsFields/fieldTypes.js"; // Import TIPOS_DATO

const AddSintomasPorPatologia = () => {
  const [editandoRelacion, setEditandoRelacion] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("activas");
  const [SintomasPorPatologia, setSintomasPorPatologia] = useState([]);
  const [Patologias, setPatologias] = useState([]);
  const [IdPatologia, setIdPatologia] = useState("");
  const [Sintomas, setSintomas] = useState([]);
  const [IdSintoma, setIdSintoma] = useState("");
  const [fieldDefinition, setFieldDefinition] = useState({});

  useEffect(() => {
    getPatologias();
    getSintomas();
  }, []);

  useEffect(() => {
    setIdSintoma("");
    getSintomasPorPatologia();
  }, [IdPatologia]);

  const getPatologias = async () => {
    try {
      const response = await api.get(`/patologias`);
      const activas = response.data.filter((p) => p.estado === 1);
      setPatologias(activas);
    } catch (error) {
      console.error("Error al obtener patologías:", error);
    }
  };

  const getSintomas = async () => {
    try {
      const response = await api.get(`/sintomas`);
      const activos = response.data.filter((s) => s.estado === 1);
      setSintomas(activos);
    } catch (error) {
      console.error("Error al obtener síntomas:", error);
    }
  };

  const getSintomasPorPatologia = async () => {
    if (!IdPatologia) return;
    try {
      const response = await api.get(`/sintomasporpatologia/${IdPatologia}`);
      setSintomasPorPatologia(response.data);
    } catch (error) {
      console.error("Error al obtener síntomas por patología:", error);
    }
  };

  const createSintomaPorPatologia = async (e) => {
    e.preventDefault();

    if (!IdPatologia) {
      return Swal.fire("Campos incompletos", "Debe seleccionar una patología.", "warning");
    }

    if (!IdSintoma) {
      return Swal.fire("Campos incompletos", "Debe seleccionar un síntoma.", "warning");
    }

    // Validar que se haya configurado el tipo de dato
    if (!fieldDefinition.tipo) {
      return Swal.fire("Configuración incompleta", "Debe configurar el tipo de dato para el síntoma.", "warning");
    }
    
    // Si es de tipo selección, validar que tenga opciones.
    if (
      [TIPOS_DATO.SELECCION, TIPOS_DATO.MULTIPLE].includes(fieldDefinition.tipo) &&
      (!fieldDefinition.configuracion?.opciones || fieldDefinition.configuracion.opciones.length === 0)
    ) {
      return Swal.fire("Configuración incompleta", "Para tipos de selección, debe agregar al menos una opción.", "warning");
    }

    const yaExiste = SintomasPorPatologia.some((item) => item.IdSintoma === IdSintoma);
    if (yaExiste) {
      return Swal.fire("Duplicado", "Este síntoma ya está asociado a la patología.", "error");
    }

    try {
      await api.post(`/sintomasporpatologia`, {
        IdPatologia,
        IdSintoma: IdSintoma,
        json_definicion: {
          tipo: fieldDefinition.tipo,
          configuracion: fieldDefinition.configuracion
        }
      });
      
      Swal.fire("Éxito", "El síntoma se asoció correctamente a la patología.", "success");
      getSintomasPorPatologia();
      setIdSintoma("");
      setFieldDefinition({}); // Resetear la definición
    } catch (error) {
      console.error("Error al guardar:", error);
      Swal.fire("Error", "Ocurrió un error al guardar. Intente nuevamente.", "error");
    }
  };

  const cambiarEstado = async (id) => {
    try {
      const response = await api.put(`/sintomaPatologiaToggleEstado/${id}`);
      const nuevoEstado = response.data.estado === 1 ? "activado" : "desactivado";
      Swal.fire("Actualizado", `El síntoma ha sido ${nuevoEstado} correctamente`, "success");
      getSintomasPorPatologia();
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      Swal.fire("Error", "No se pudo cambiar el estado", "error");
    }
  };

  const handleEditar = async (valores) => {
    try {
      // Permitir editar también json_definicion (parametrizable)
      await api.put(`/sintomasporpatologia/${valores.IdSintomaPorPatologia}`, {
        IdSintoma: valores.IdSintoma,
        Nombre: valores.Nombre,
        Descripcion: valores.Descripcion || "",
        json_definicion: valores.json_definicion || {},
      });
      Swal.fire("Actualizado", "El síntoma por patología ha sido editado correctamente", "success");
      setEditandoRelacion(null);
      getSintomasPorPatologia();
      getSintomas();
    } catch (error) {
      console.error("Error al editar:", error.response?.data || error.message);
      Swal.fire("Error", "No se pudo actualizar el síntoma por patología", "error");
    }
  };

  const editFields = [
    {
      name: "Nombre",
      label: "Nombre",
      type: "text",
      required: true,
    },
    {
      name: "Descripcion",
      label: "Descripción",
      type: "textarea",
      required: false,
    },
    {
      name: "json_definicion",
      label: "Configuración del campo",
      type: "custom",
      component: FieldDefinition,
      componentProps: {
        allSymptoms: Sintomas,
        sintomasPorPatologia: SintomasPorPatologia,
      },
      required: false,
    },
  ];

  const filtrarPorEstado = () => {
    if (filtroEstado === "todas") return SintomasPorPatologia;
    if (filtroEstado === "activas") return SintomasPorPatologia.filter((s) => s.estado === 1);
    if (filtroEstado === "inactivas") return SintomasPorPatologia.filter((s) => s.estado === 0);
    return SintomasPorPatologia;
  };
  useEffect(() => {
  let mensaje = "";
  switch (filtroEstado) {
    case "activas":
      mensaje = "Mostrando relaciones activas.";
      break;
    case "inactivas":
      mensaje = "Mostrando relaciones inactivas.";
      break;
    case "todas":
      mensaje = "Mostrando todas las relaciones.";
      break;
    default:
      break;
  }

  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "info",
    title: mensaje,
    showConfirmButton: false,
    timer: 1800,
    timerProgressBar: true,
  });
}, [filtroEstado]);

  const columnas = [
    {
      name: "Síntoma",
      selector: (row) => row?.NombreSintoma || "No disponible",
      sortable: true,
    },
    {
      name: "Tipo de dato",
      selector: (row) => {
        let def = row.json_definicion || {};
        if (typeof def === 'string') { try { def = JSON.parse(def); } catch { def = {}; } }
        return def.tipo || "No configurado";
      },
      sortable: true,
    },
    {
      name: "Acciones",
      cell: (row) => (
        <>
          <button
            type="button"
            className="button is-small is-info mr-2"
            title="Editar"
            onClick={() => setEditandoRelacion({
              IdSintoma: row.IdSintoma,
              Nombre: row.NombreSintoma,
              Descripcion: row.Descripcion || "",
            })}
          >
            <FontAwesomeIcon icon={faPencil} />
          </button>

          {row.estado === 1 ? (
            <FontAwesomeIcon
             icon={faSquarePlus}
              style={{ color: "#37ff00", cursor: "pointer", marginLeft: "10px" }}
              size="2xl"
              title="Desactivar"
              onClick={() => cambiarEstado(row.IdSintomaPorPatologia)}
            />
          ) : (
            <FontAwesomeIcon
              icon={faSquareMinus}
              style={{ color: "#ff0000", cursor: "pointer", marginLeft: "10px" }}
              size="2xl"
              title="Activar"
              onClick={() => cambiarEstado(row.IdSintomaPorPatologia)}
            />
          )}
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      compact: true,
    },
  ];

  const paginationOptionsES = {
    rowsPerPageText: "Filas por página",
    rangeSeparatorText: "de",
    selectAllRowsItem: true,
    selectAllRowsItemText: "Todos",
  };

  return (
    <div className="columns mt-5 is-centered">
      <div className="column is-half">
        <div className="notification is-primary">
          <strong>Síntomas por patología</strong>
        </div>

        <form onSubmit={createSintomaPorPatologia}>
          <div className="field">
            <label className="label required">Seleccionar Patología</label>
            <div className="select is-fullwidth">
              <select
                value={IdPatologia}
                onChange={(e) => setIdPatologia(parseInt(e.target.value))}
                required
              >
                <option value="">Seleccionar</option>
                {Patologias.map((p) => (
                  <option key={p.IdPatologia} value={p.IdPatologia}>
                    {p.Nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label className="label required">Síntomas</label>
            <div className="select is-fullwidth">
              <select
                value={IdSintoma}
                onChange={(e) => setIdSintoma(parseInt(e.target.value))}
                required
              >
                <option value="">Seleccionar</option>
                {Sintomas.map((s) => (
                  <option key={s.IdSintoma} value={s.IdSintoma}>
                    {s.Nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label className="label required">Configuración del campo</label>
            <FieldDefinition 
              inicial={fieldDefinition}
              onChange={setFieldDefinition}
              allSymptoms={Sintomas}
              sintomasPorPatologia={SintomasPorPatologia}
            />
          </div>

          <div className="field mt-3">
            <button type="submit" className="button is-success">
              Guardar
            </button>
          </div>

          <div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
            {["todas", "activas", "inactivas"].map((estado) => (
              <button
                key={estado}
                onClick={() => setFiltroEstado(estado)}
                type="button"
                style={{
                  padding: "6px 16px",
                  border: "none",
                  borderRadius: "20px",
                  backgroundColor: filtroEstado === estado ? "#d1f5d3" : "#f0f2f5",
                  color: filtroEstado === estado ? "#075e54" : "#333",
                  fontWeight: filtroEstado === estado ? "bold" : "normal",
                  boxShadow: filtroEstado === estado ? "inset 0 0 0 1px #1ebea5" : "inset 0 0 0 1px #ccc",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  fontSize: "0.9rem",
                }}
              >
                {estado.charAt(0).toUpperCase() + estado.slice(1)}
              </button>
            ))}
          </div>

          <div className="mt-4">
            <DataTable
              pagination
              paginationComponentOptions={paginationOptionsES}
              columns={columnas}
              data={filtrarPorEstado()}
            />
          </div>
        </form>

        {editandoRelacion && (
          <EditModal
            fields={editFields}
            initialValues={{
              ...editandoRelacion,
              json_definicion: (() => { const raw = SintomasPorPatologia.find(s => s.IdSintoma === editandoRelacion.IdSintoma)?.json_definicion || {}; if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return {}; } } return raw; })(),
              IdSintomaPorPatologia: SintomasPorPatologia.find(s => s.IdSintoma === editandoRelacion.IdSintoma)?.IdSintomaPorPatologia,
            }}
            onSubmit={handleEditar}
            onClose={() => setEditandoRelacion(null)}
            title="Editar Síntoma por Patología"
          />
        )}
      </div>
    </div>
  );
};

export default AddSintomasPorPatologia;